/**
 * HustleVoice — in-app voice assistant component.
 *
 * Single mic button that drops into any page.  Uses:
 *   • Web Speech API (window.SpeechRecognition) for capture in PWA
 *   • SpeechSynthesis API for spoken replies
 *   • /api/voice/intent for one-shot commands
 *   • /api/voice/converse for multi-turn shopping
 *
 * In the native Capacitor wrapper (later), swap window.SpeechRecognition
 * for @capacitor-community/speech-recognition without touching the
 * caller — the same hook signature is preserved.
 *
 * Usage:
 *   import HustleVoice from "@/components/voice/HustleVoice";
 *   <HustleVoice module="plaza" />
 *
 * Personalisation: greets the user by first name (pulled from
 * /api/identity/me or /api/voice/greeting).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Mic, MicOff, Loader2, X, MessageCircle, EyeOff } from "lucide-react";
import { voiceLocaleFor } from "@/i18n";
import { useVoiceActions } from "@/context/VoiceActionsContext";
import "./HustleVoice.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API, withCredentials: true });

// Routes where the floating mic covers submit buttons on a full-canvas
// editor — hide the FAB entirely on those pages.
const HIDE_ON_ROUTES = [
  /^\/seatingmaker\/edit\//,
  /^\/tickets\/venue\//,
];

const HIDE_KEY = "hustle-voice-hidden-at";
const NUDGE_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

export default function HustleVoice({ module = "hustle" }) {
  const [open, setOpen]         = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy]         = useState(false);
  const [greeting, setGreeting] = useState("Tap to talk.");
  const [reply, setReply]       = useState("");
  const [history, setHistory]   = useState([]);
  const [supported, setSupported] = useState(true);
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    const at = parseInt(window.localStorage.getItem(HIDE_KEY) || "0", 10);
    return Date.now() - at < NUDGE_AFTER_MS;
  });
  const recognitionRef          = useRef(null);
  const sessionIdRef            = useRef(`vs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const navigate                = useNavigate();
  const location                = useLocation();
  const { i18n }                = useTranslation();
  const voiceLang               = voiceLocaleFor(i18n.language || "en");
  const { dispatch: dispatchVoiceAction } = useVoiceActions();

  /* ── Set up Web Speech API ────────────────────────────────── */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.continuous     = false;
    rec.interimResults = true;
    rec.lang           = voiceLang;
    rec.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join("");
      setTranscript(t);
      if (e.results[0]?.isFinal) {
        setListening(false);
        handleUtterance(t);
      }
    };
    rec.onerror = () => setListening(false);
    rec.onend   = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch (_) { /* ignore */ } };
    // Re-build recogniser if the user changes language mid-session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceLang]);

  /* ── Personalised greeting (in user's language) ───────────── */
  useEffect(() => {
    if (!open) return;
    api.get(`/api/voice/greeting?module=${module}&lang=${i18n.language || "en"}`)
      .then((r) => setGreeting(r.data.greeting))
      .catch(() => setGreeting("Tap the mic and ask me anything."));
  }, [open, module, i18n.language]);

  /* ── Speak a reply using browser TTS (in user's language) ─ */
  const speak = (text) => {
    if (!text || !("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang  = voiceLang;
    u.rate  = 1.0;
    u.pitch = 1.0;
    // Pick best matching system voice for this locale
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang === voiceLang)
               || voices.find((v) => v.lang.startsWith(voiceLang.split("-")[0]));
    if (match) u.voice = match;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  /* ── Start / stop mic ─────────────────────────────────────── */
  const startListening = () => {
    setTranscript("");
    setReply("");
    try {
      recognitionRef.current?.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };
  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (_) { /* ignore */ }
    setListening(false);
  };

  /* ── Send transcript to backend → execute action ──────────── */
  const handleUtterance = async (text) => {
    if (!text || text.trim().length < 2) return;
    setBusy(true);
    setHistory((h) => [...h, { role: "user", text }]);

    // 1) Try page-registered voice actions first (deterministic, instant)
    const va = dispatchVoiceAction(text);
    if (va.matched) {
      const ack = `Got it — ${va.label || "done"}.`;
      setReply(ack);
      setHistory((h) => [...h, { role: "hustle", text: ack }]);
      speak(ack);
      setBusy(false);
      return;
    }

    // 2) Fall through to LLM-based intent
    try {
      const r = await api.post(`/api/voice/intent`, {
        utterance: text,
        module,
        page: window.location.pathname,
        lang: i18n.language || "en",
      });
      const a = r.data;
      const replyText = a.reply || "";
      if (replyText) {
        setReply(replyText);
        setHistory((h) => [...h, { role: "hustle", text: replyText }]);
        speak(replyText);
      }
      // Dispatch action
      if (a.action === "navigate" && a.route) {
        setTimeout(() => { navigate(a.route); setOpen(false); }, 1200);
      } else if (a.action === "search" && a.module && a.q) {
        const routeMap = {
          "tackle-shop": `/tackle?q=${encodeURIComponent(a.q)}`,
          "plaza":       `/plaza?q=${encodeURIComponent(a.q)}`,
          "fish-map":    `/fish-map?q=${encodeURIComponent(a.q)}`,
          "hustleup":    `/up/groups?q=${encodeURIComponent(a.q)}`,
        };
        const route = routeMap[a.module];
        if (route) setTimeout(() => { navigate(route); setOpen(false); }, 1200);
      } else if (a.action === "add_to_cart") {
        const r2 = await api.post(`/api/voice/converse`, {
          utterance: text,
          session_id: sessionIdRef.current,
          module: "plaza",
        });
        const c = r2.data;
        if (c.reply) { setReply(c.reply); speak(c.reply); }
      }
    } catch (err) {
      const msg = "Sorry, I didn't catch that. Try again?";
      setReply(msg); speak(msg);
    }
    setBusy(false);
  };

  if (!supported) return null;  // browser doesn't support speech recognition
  if (hidden) return null;
  if (HIDE_ON_ROUTES.some((re) => re.test(location.pathname || ""))) {
    return null;  // full-canvas editor route — the mic FAB covers submit buttons
  }

  const dismissFab = () => {
    try { window.localStorage.setItem(HIDE_KEY, String(Date.now())); } catch (_e) {}
    setHidden(true);
    setOpen(false);
  };

  return (
    <>
      {/* Floating mic FAB with permanent-hide sibling */}
      {!open && (
        <div className="hv-fab-wrap">
          <button
            className="hv-fab-hide"
            onClick={dismissFab}
            data-testid="hustle-voice-hide"
            aria-label="Hide voice assistant for 14 days"
            title="Hide for 14 days"
          >
            <EyeOff size={12} />
          </button>
          <button
            className="hv-fab"
            onClick={() => setOpen(true)}
            data-testid="hustle-voice-fab"
            aria-label="Open Hustle Voice"
          >
            <Mic size={22} />
          </button>
        </div>
      )}

      {open && (
        <div className="hv-panel" data-testid="hustle-voice-panel">
          <button
            className="hv-close"
            onClick={() => { setOpen(false); stopListening(); }}
            aria-label="Close"
            data-testid="hustle-voice-close"
          >
            <X size={16} />
          </button>
          <div className="hv-greeting" data-testid="hustle-voice-greeting">
            {greeting}
          </div>

          {transcript && (
            <div className="hv-bubble hv-bubble--user">
              <MessageCircle size={12}/> {transcript}
            </div>
          )}
          {reply && (
            <div className="hv-bubble hv-bubble--hustle">
              {reply}
            </div>
          )}
          {busy && (
            <div className="hv-status">
              <Loader2 className="hv-spin" size={14}/> Thinking…
            </div>
          )}

          <div className="hv-controls">
            <button
              className={`hv-mic ${listening ? "hv-mic--on" : ""}`}
              onClick={listening ? stopListening : startListening}
              data-testid="hustle-voice-mic"
              disabled={busy}
            >
              {listening ? <MicOff size={20}/> : <Mic size={20}/>}
              <span>{listening ? "Listening…" : "Tap to speak"}</span>
            </button>
          </div>

          <div className="hv-hints">
            Try: <em>&quot;find me a slippery slider&quot;</em>,
            <em> &quot;show barra spots in QLD&quot;</em>,
            <em> &quot;groups in Perth&quot;</em>
          </div>
        </div>
      )}
    </>
  );
}
